import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';

// VIP 플랜 페이지 움직임 부품 (2026-10-14 대표 "애니메이션이나 세계 최고 기법 등을 통해서 더 개선할 수 있지 않아?")
// 원칙: 움직임은 '의미'가 있을 때만(숫자가 쌓이고, 새 조회자가 도착하고, 결제 버튼이 숨 쉰다).
// transform·opacity만 움직여 버벅임을 막고, 휴대폰 '동작 줄이기' 설정이면 전부 정지 화면으로 보여 준다.

/** 화면에 보이는 순간 0에서 목표 숫자까지 올라간다 (WHOOP·Cal AI 점수 공개 문법) */
export const CountUp: React.FC<{ to: number; duration?: number; delay?: number; className?: string }> = ({
    to, duration = 1.1, delay = 0, className,
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    const inView = useInView(ref, { once: true, margin: '-40px' });
    const reduce = useReducedMotion();
    const [value, setValue] = useState(reduce ? to : 0);

    useEffect(() => {
        if (!inView) return;
        if (reduce) { setValue(to); return; }
        const controls = animate(0, to, {
            duration, delay, ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setValue(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to, duration, delay, reduce]);

    return <span ref={ref} className={className}>{value}</span>;
};

const SCRAMBLE_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789_.';

/** 글자가 뒤섞이다 제자리를 찾는다 — 가려진 아이디가 '해독되는' 느낌 (Linear·Stripe 히어로 기법) */
export const ScrambleText: React.FC<{ text: string; className?: string; duration?: number }> = ({
    text, className, duration = 0.9,
}) => {
    const reduce = useReducedMotion();
    const [shown, setShown] = useState(reduce ? text : '');

    useEffect(() => {
        if (reduce) { setShown(text); return; }
        let frame = 0;
        const total = Math.max(12, Math.round(duration * 60));
        let raf = 0;
        const tick = () => {
            frame += 1;
            const settled = Math.floor((frame / total) * text.length);
            const out = text.split('').map((ch, i) => {
                if (i < settled || ch === '*' || ch === '@') return ch;
                return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
            }).join('');
            setShown(out);
            if (frame < total) raf = requestAnimationFrame(tick);
            else setShown(text);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [text, duration, reduce]);

    return <span className={className}>{shown || text}</span>;
};

/** 결제 버튼 — 흰 캡슐 둘레로 빛이 천천히 숨 쉬듯 번진다(어두운 바탕 위 광채) + 누르면 살짝 들어간다 */
export const ShineButton: React.FC<{
    children: React.ReactNode; className?: string; disabled?: boolean; onClick?: () => void;
    /** 폼 제출 단추로 쓸 때만 'submit'(2026-11-03 로그인 화면) — 기본은 예전 그대로 'button' */
    type?: 'button' | 'submit';
}> = ({ children, className = '', disabled, onClick, type = 'button' }) => {
    const reduce = useReducedMotion();
    const glow = !reduce && !disabled;
    return (
        <motion.button
            type={type}
            onClick={onClick}
            whileTap={disabled || reduce ? undefined : { scale: 0.98 }}
            disabled={disabled}
            className={`relative ${className}`}
            animate={glow ? {
                boxShadow: [
                    '0 0 0px 0px rgba(255,255,255,0.00)',
                    '0 0 30px 2px rgba(255,255,255,0.28)',
                    '0 0 0px 0px rgba(255,255,255,0.00)',
                ],
            } : undefined}
            transition={glow ? { duration: 3.2, repeat: Infinity, ease: 'easeInOut' } : undefined}
        >
            <span className="relative z-10 flex items-center justify-center gap-2">{children}</span>
        </motion.button>
    );
};

const REVEAL_FROM = { opacity: 0, y: 22 };
const REVEAL_TO = { opacity: 1, y: 0 };
const REVEAL_VIEWPORT = { once: true, margin: '-60px' };

/** 스크롤해서 보일 때 아래에서 떠오르며 나타난다(한 번만) */
export const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number; as?: 'section' | 'div' | 'li' }> = ({
    children, className, delay = 0, as = 'div',
}) => {
    const reduce = useReducedMotion();
    const motionProps = reduce ? {} : {
        initial: REVEAL_FROM,
        whileInView: REVEAL_TO,
        viewport: REVEAL_VIEWPORT,
        transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
    };
    if (as === 'section') return <motion.section className={className} {...motionProps}>{children}</motion.section>;
    if (as === 'li') return <motion.li className={className} {...motionProps}>{children}</motion.li>;
    return <motion.div className={className} {...motionProps}>{children}</motion.div>;
};

/** 마우스를 따라 카드가 살짝 기울고 빛이 따라온다 (PC 전용 — 터치 기기는 그대로) */
export const TiltCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
    const reduce = useReducedMotion();
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), { stiffness: 160, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 160, damping: 18 });
    const glowX = useTransform(mx, (v) => `${v * 100}%`);
    const glowY = useTransform(my, (v) => `${v * 100}%`);
    const glow = useTransform([glowX, glowY], ([x, y]) =>
        `radial-gradient(420px circle at ${x} ${y}, rgba(255,255,255,0.07), transparent 45%)`);

    if (reduce) return <div className={className}>{children}</div>;
    return (
        <motion.div
            className={className}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            onPointerMove={(e) => {
                if (e.pointerType !== 'mouse') return;
                const r = e.currentTarget.getBoundingClientRect();
                mx.set((e.clientX - r.left) / r.width);
                my.set((e.clientY - r.top) / r.height);
            }}
            onPointerLeave={() => { mx.set(0.5); my.set(0.5); }}
        >
            {children}
            <motion.div aria-hidden className="pointer-events-none absolute inset-0 rounded-[24px]" style={{ background: glow }} />
        </motion.div>
    );
};
